import * as React from 'react'

import {
  Box,
  Button,
  Chevron,
  DirectionType,
  Text,
  Touchable,
  useClassNames,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { CardHeader } from 'modules/core/components/OldCard'
import Popover from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import Counter from './Counter'
import './index.styles.css'

const stylesRefs = {
  invisible: 'Navigator_invisible',
  'filename.withAfterFilenameContent': 'Navigator_filename_withAfterFilenameContent',
  'filename.onlyName': 'Navigator_filename_onlyName',
}

interface Props {
  title: string
  index?: number
  total?: number
  onCancel?: (event: React.SyntheticEvent) => void
  onClickBack?: () => void
  onClickForward?: () => void
  showCounter?: boolean
  closeAriaLabel?: string
  actions?: JSX.Element[]
  useLeftClose?: boolean
}
interface PropsNavigationButton {
  className?: string
  direction: DirectionType
  onClick: (event: React.SyntheticEvent) => void
}

const NavigationButton = ({ direction, onClick, className }: PropsNavigationButton) => {
  const ariaLabel =
    direction === 'right'
      ? i18n.t('documents.modal.next_document')
      : direction === 'left'
        ? i18n.t('documents.modal.prev_document')
        : ''

  return (
    <Box className={className}>
      <Touchable onClick={onClick} aria-label={ariaLabel}>
        <Chevron type="secondary" size="small" direction={direction} />
      </Touchable>
    </Box>
  )
}

const Navigator = ({
  title,
  index = 0,
  total = 0,
  onCancel,
  onClickBack,
  onClickForward,
  showCounter,
  closeAriaLabel,
  actions,
  useLeftClose = false,
}: Props) => {
  const cx = useClassNames(stylesRefs)
  const hidePrevious = index <= 0
  const hideNext = index >= total - 1

  return (
    <CardHeader
      middle={
        <Box flexDirection="row" flexGrow paddingX="s32">
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="spaceBetween"
            paddingX="s24"
            flexGrow
          >
            {!!onClickBack && (
              <NavigationButton
                direction="left"
                onClick={onClickBack}
                className={cx({ invisible: hidePrevious })}
              />
            )}
            <Box flexDirection="row" alignItems="center" paddingX="s12" gap="s12">
              <Box
                flexDirection="row"
                alignItems="center"
                className={cx({
                  'filename.withAfterFilenameContent': showCounter,
                  'filename.onlyName': !showCounter,
                })}
              >
                <Text.Normal weight="semibold" ellipsis>
                  {title}
                </Text.Normal>
              </Box>
              {showCounter ? <Counter index={index} total={total} /> : null}
            </Box>
            {!!onClickForward && (
              <NavigationButton
                direction="right"
                onClick={onClickForward}
                className={cx({ invisible: hideNext })}
              />
            )}
          </Box>
        </Box>
      }
      left={
        <>
          {(!!actions?.length || useLeftClose) && !!onCancel && (
            <Button
              onClick={onCancel}
              type="secondary"
              aria-label={closeAriaLabel}
              iconName="cross"
            />
          )}
        </>
      }
      right={
        <>
          {actions?.length ? (
            <Popover type="list">
              <Popover.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </Popover.Trigger>
              <Popover.Content side="bottom" align="end">
                {actions}
              </Popover.Content>
            </Popover>
          ) : (
            <>
              {!!onCancel && !useLeftClose && (
                <Button
                  onClick={onCancel}
                  type="secondary"
                  aria-label={closeAriaLabel}
                  iconName="cross"
                />
              )}
            </>
          )}
        </>
      }
    />
  )
}

export default observer(Navigator)
